import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiX, FiPlay, FiPause, FiRotateCcw, FiRepeat } from "react-icons/fi";

// A full-screen teleprompter: the narration scrolls by itself at the speed chosen, big and centred, with a line that marks where to read.
export default function Teleprompter({ text, wordsPerSecond = 2.5, onClose }) {
  const box = useRef(null);
  const frame = useRef(0);
  const last = useRef(0);
  const position = useRef(0);
  const [running, setRunning] = useState(false);
  const [size, setSize] = useState(56);
  const [speed, setSpeed] = useState(100);       // percent of the speaking speed of the script
  const [mirror, setMirror] = useState(false);
  const [done, setDone] = useState(false);

  // pixels per second so that the text keeps pace with a presenter who speaks wordsPerSecond words a second
  const pixelsPerSecond = useCallback(() => {
    const element = box.current;
    if (!element) return 40;
    const words = Math.max(1, text.split(/\s+/).filter(Boolean).length);
    const scrollable = Math.max(1, element.scrollHeight - element.clientHeight);
    return (scrollable / (words / wordsPerSecond)) * (speed / 100);
  }, [text, wordsPerSecond, speed]);

  useEffect(() => {
    if (!running) return undefined;
    last.current = performance.now();
    const step = (now) => {
      const element = box.current;
      if (!element) return;
      position.current += ((now - last.current) / 1000) * pixelsPerSecond();
      last.current = now;
      element.scrollTop = position.current;
      if (position.current >= element.scrollHeight - element.clientHeight - 1) { setRunning(false); setDone(true); return; }
      frame.current = requestAnimationFrame(step);
    };
    frame.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame.current);
  }, [running, pixelsPerSecond]);

  const restart = () => { position.current = 0; if (box.current) box.current.scrollTop = 0; setDone(false); setRunning(false); };
  const toggle = useCallback(() => { setDone(false); setRunning((r) => !r); }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === " " && e.target.tagName !== "INPUT") { e.preventDefault(); toggle(); }
      else if (e.key === "ArrowUp") setSpeed((s) => Math.min(250, s + 10));
      else if (e.key === "ArrowDown") setSpeed((s) => Math.max(30, s - 10));
    };
    window.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = previous; };
  }, [onClose, toggle]);

  // if the visitor scrolls by hand, the automatic scrolling carries on from there
  const onScroll = (e) => { if (!running) position.current = e.currentTarget.scrollTop; };
  const paragraphs = text.split(/\n{2,}|\n/).filter((p) => p.trim());

  return (
    <div className="fixed inset-0 z-[100] bg-black text-white flex flex-col" role="dialog" aria-modal="true" aria-label="Teleprompter">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 border-b border-white/15 bg-black/90 text-sm">
        <button type="button" onClick={toggle} className="px-4 py-2 rounded-full bg-brand-gradient font-semibold inline-flex items-center gap-2">{running ? <><FiPause /> Pause</> : <><FiPlay /> {done ? "Play again" : "Start"}</>}</button>
        <button type="button" onClick={restart} className="p-2 rounded-full border border-white/20 hover:border-accent-light" aria-label="Back to the start" title="Back to the start"><FiRotateCcw /></button>
        <label className="flex items-center gap-2">Speed <input type="range" min="30" max="250" step="10" value={speed} onChange={(e) => setSpeed(Number(e.target.value))} className="accent-[#22d3ee] w-32" aria-label="Speed" /><span className="tabular-nums w-10 text-gray-400">{speed}%</span></label>
        <label className="flex items-center gap-2">Size <input type="range" min="28" max="110" step="2" value={size} onChange={(e) => setSize(Number(e.target.value))} className="accent-[#22d3ee] w-32" aria-label="Text size" /></label>
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={mirror} onChange={(e) => setMirror(e.target.checked)} className="accent-[#22d3ee] w-4 h-4" /> <FiRepeat /> Mirror</label>
        <span className="text-xs text-gray-500 hidden md:inline">Space = start / pause · Up / Down = speed · Esc = close</span>
        <button type="button" onClick={onClose} className="ml-auto p-2 rounded-full border border-white/20 hover:border-red-400" aria-label="Close the teleprompter"><FiX /></button>
      </div>
      <div className="relative flex-1 min-h-0">
        <div className="pointer-events-none absolute inset-x-0 top-[38%] h-[1px] bg-accent-light/60 z-10" aria-hidden="true" />
        <div className="pointer-events-none absolute left-0 top-[38%] -translate-y-1/2 w-0 h-0 border-y-[10px] border-y-transparent border-l-[16px] border-l-accent-light z-10" aria-hidden="true" />
        <div ref={box} onScroll={onScroll} className="h-full overflow-y-auto px-[8%]" style={{ transform: mirror ? "scaleX(-1)" : "none", scrollBehavior: "auto" }}>
          <div style={{ paddingTop: "38vh", paddingBottom: "70vh", fontSize: `${size}px`, lineHeight: 1.45, fontWeight: 600, maxWidth: "1100px", margin: "0 auto" }}>
            {paragraphs.map((p, i) => <p key={i} dir="auto" style={{ marginBottom: "0.9em" }}>{p}</p>)}
          </div>
        </div>
      </div>
    </div>
  );
}
